/**
 * The one feedback system, used by every page.
 *
 * The legacy app ran three in parallel — a toast context, a second local toast in Org
 * Review, and 24 surviving native alert/confirm/prompt calls. `no-alert` and
 * `no-restricted-globals` now make that a lint error; this is the replacement.
 *
 * WHAT THE PRESENTATION ADDS over the first version, which was a bordered box
 * with an icon and a line of text:
 *  - A tone heading, so the outcome reads at a glance instead of being inferred
 *    from a 3px border colour. Callers may override it.
 *  - Distinct warning and info tones. Both previously reused the error colour,
 *    so a warning looked identical to a failure.
 *  - A countdown rail showing the time left, which pauses while the pointer is
 *    over the stack or focus is inside it — a message must not expire mid-read.
 *  - An optional action (Retry, Undo, View) so a notification can be acted on
 *    rather than only acknowledged.
 *  - Enter and exit animations, and a cap on how many show at once, so a burst
 *    of responses does not bury the screen.
 *
 * The call signatures are unchanged — `toast.success('…')` and `toast.error(err)`
 * work exactly as before across all 28 call sites; options are additive.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { errorMessage } from '@/lib/api/errors';
import { Button } from '@/ui/Button';
import { ToastContext } from './ToastContext';
import type {
  ToastAction,
  ToastContextValue,
  ToastOptions,
  ToastPresentation,
  ToastTone,
} from './ToastContext';
import styles from './ToastProvider.module.css';

export type {
  ToastTone,
  ToastPresentation,
  ToastAction,
  ToastOptions,
  ToastContextValue,
} from './ToastContext';
export { ToastContext, useToast, useOptionalToast } from './ToastContext';

interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
  /** Absent means the message stands alone as the headline. */
  readonly title?: string | undefined;
  readonly icon?: ComponentType<{ size?: number; strokeWidth?: number; className?: string }> | undefined;
  readonly action?: ToastAction | undefined;
  readonly duration: number | null;
  readonly presentation: ToastPresentation;
  /** Set while the exit animation plays, just before removal. */
  readonly leaving?: boolean;
}

const AUTO_DISMISS_MS: Record<ToastTone, number | null> = {
  success: 6000,
  info: 7000,
  warning: 7000,
  // Errors clear themselves too: a pinned toast stacked up and had to be
  // dismissed by hand. A caller with something worth keeping on screen can
  // still pass duration: null.
  error: 7000,
};

/**
 * Only errors get a standing heading. For the other tones a generic word above
 * the sentence ("Done" over "Deactivated temp.user") repeats the message and
 * costs a line, so the message itself is the headline unless the caller passes
 * a title worth having. An error is different: the heading names the category
 * while the sentence carries the detail, which is often long or technical.
 */
const DEFAULT_TITLE: Partial<Record<ToastTone, string>> = {
  error: 'Something went wrong',
};

const ICONS: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

/** How many show at once. Older ones retire first so a burst cannot bury the page. */
const MAX_VISIBLE = 4;
/** Must match the exit animation in the stylesheet (slow graceful exit). */
const EXIT_MS = 600;

interface TimerEntry {
  timer: ReturnType<typeof setTimeout> | null;
  startedAt: number;
  remaining: number;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const toastsRef = useRef<readonly Toast[]>([]);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  pausedRef.current = paused;
  const nextId = useRef(1);
  const timers = useRef(new Map<number, TimerEntry>());
  const exits = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const updateToasts = useCallback((updater: (prev: readonly Toast[]) => readonly Toast[]) => {
    setToasts((current) => {
      const next = updater(current);
      toastsRef.current = next;
      return next;
    });
  }, []);

  const remove = useCallback(
    (id: number) => {
      const entry = timers.current.get(id);
      if (entry?.timer !== null && entry?.timer !== undefined) {
        clearTimeout(entry.timer);
      }
      timers.current.delete(id);
      const exitTimer = exits.current.get(id);
      if (exitTimer !== undefined) {
        clearTimeout(exitTimer);
        exits.current.delete(id);
      }
      updateToasts((current) => current.filter((toast) => toast.id !== id));
    },
    [updateToasts],
  );

  const dismiss = useCallback(
    (id: number) => {
      const entry = timers.current.get(id);
      if (entry !== undefined) {
        if (entry.timer !== null) {
          clearTimeout(entry.timer);
        }
        timers.current.delete(id);
      }
      // Already on its way out — don't restart the exit.
      if (exits.current.has(id)) return;
      updateToasts((current) =>
        current.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast)),
      );
      exits.current.set(
        id,
        setTimeout(() => remove(id), EXIT_MS),
      );
    },
    [remove, updateToasts],
  );

  useEffect(() => {
    const activeTimers = timers.current;
    const activeExits = exits.current;
    return () => {
      activeTimers.forEach((entry) => {
        if (entry.timer !== null) clearTimeout(entry.timer);
      });
      activeTimers.clear();
      activeExits.forEach((timer) => clearTimeout(timer));
      activeExits.clear();
    };
  }, []);

  // Pointer over the stack, or focus inside it, holds countdowns.
  // When pointer leaves or focus blurs, resume countdowns with remaining time.
  useEffect(() => {
    const activeTimers = timers.current;
    if (paused) {
      activeTimers.forEach((entry) => {
        if (entry.timer !== null) {
          clearTimeout(entry.timer);
          entry.timer = null;
          const elapsed = Date.now() - entry.startedAt;
          entry.remaining = Math.max(1000, entry.remaining - elapsed);
        }
      });
    } else {
      activeTimers.forEach((entry, id) => {
        if (entry.timer === null && entry.remaining > 0) {
          entry.startedAt = Date.now();
          entry.timer = setTimeout(() => dismiss(id), entry.remaining);
        }
      });
    }
  }, [paused, dismiss]);

  const notify = useCallback(
    (tone: ToastTone, message: string, options?: ToastOptions) => {
      const presentation = options?.presentation ?? 'toast';
      const duration =
        options?.duration !== undefined ? options.duration : AUTO_DISMISS_MS[tone];

      // If an identical message is already actively displayed in the same presentation,
      // refresh its countdown rather than stacking duplicate cards.
      const existing = toastsRef.current.find(
        (t) =>
          !t.leaving &&
          t.tone === tone &&
          t.message === message &&
          t.presentation === presentation,
      );
      if (existing) {
        if (duration !== null) {
          const entry = timers.current.get(existing.id);
          if (entry) {
            if (entry.timer !== null) clearTimeout(entry.timer);
            entry.remaining = duration;
            entry.startedAt = Date.now();
            if (!pausedRef.current) {
              entry.timer = setTimeout(() => dismiss(existing.id), duration);
            }
          }
        }
        return existing.id;
      }

      const id = nextId.current++;
      const toast: Toast = {
        id,
        tone,
        message,
        title: options?.title ?? DEFAULT_TITLE[tone],
        icon: options?.icon,
        presentation,
        action: options?.action,
        duration,
      };

      updateToasts((current) => {
        const next = [...current, toast];
        const live = next.filter((t) => !t.leaving);
        if (live.length <= MAX_VISIBLE) return next;
        // Retire the oldest live one immediately rather than animating it out,
        // so the stack height stays stable during a burst.
        const oldest = live[0];
        if (oldest === undefined) return next;
        const entry = timers.current.get(oldest.id);
        if (entry?.timer !== null && entry?.timer !== undefined) {
          clearTimeout(entry.timer);
        }
        timers.current.delete(oldest.id);
        return next.filter((t) => t.id !== oldest.id);
      });

      if (duration !== null) {
        const entry: TimerEntry = {
          timer: null,
          startedAt: Date.now(),
          remaining: duration,
        };
        if (!pausedRef.current) {
          entry.timer = setTimeout(() => dismiss(id), duration);
        }
        timers.current.set(id, entry);
      }
      return id;
    },
    [dismiss, updateToasts],
  );

  const cornerToasts = toasts.filter((t) => t.presentation === 'toast');
  const modalToasts = toasts.filter((t) => t.presentation === 'modal');

  const value = useMemo<ToastContextValue>(
    () => ({
      notify,
      dismiss,
      success: (message: string, options?: ToastOptions) => notify('success', message, options),
      warning: (message: string, options?: ToastOptions) => notify('warning', message, options),
      info: (message: string, options?: ToastOptions) => notify('info', message, options),
      error: (error: unknown, options?: ToastOptions) =>
        notify('error', errorMessage(error), options),
    }),
    [notify, dismiss],
  );

  return (
    <ToastContext value={value}>
      {children}
      {/*
        role="status" + aria-live announces toasts to screen readers without
        stealing focus. The legacy app announced nothing.
      */}
      {/* Corner stack: top right, never blocks the page. */}
      {cornerToasts.length > 0 && (
        <div
          className={styles.viewport}
          role="status"
          aria-live="polite"
          aria-atomic="false"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          {cornerToasts.map((toast) => {
            const Icon = toast.icon ?? ICONS[toast.tone];
            return (
              <div
                key={toast.id}
                className={[
                  styles.cornerToast,
                  styles[toast.tone],
                  toast.leaving ? styles.leaving : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-testid={`toast-${toast.tone}`}
              >
                <span className={styles.cornerIcon} aria-hidden="true">
                  <Icon size={17} strokeWidth={2.1} />
                </span>

                <div className={styles.cornerBody}>
                  {toast.title ? (
                    <>
                      <p className={styles.cornerTitle}>{toast.title}</p>
                      <p className={styles.cornerMessage}>{toast.message}</p>
                    </>
                  ) : (
                    <p className={styles.cornerStandaloneMessage}>{toast.message}</p>
                  )}
                  {toast.action ? (
                    <button
                      type="button"
                      className={styles.cornerAction}
                      onClick={() => {
                        toast.action?.onClick();
                        dismiss(toast.id);
                      }}
                    >
                      {toast.action.label}
                    </button>
                  ) : null}
                </div>

                <button
                  type="button"
                  className={styles.cornerDismiss}
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss notification"
                >
                  <X size={14} aria-hidden="true" />
                </button>

                {toast.duration !== null && !toast.leaving ? (
                  <span
                    className={styles.rail}
                    aria-hidden="true"
                    style={{
                      animationDuration: `${toast.duration}ms`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {modalToasts.length > 0 && (
        <div
          className={styles.backdrop}
          role="presentation"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && modalToasts.length > 0) {
              const latest = modalToasts[modalToasts.length - 1];
              if (latest && latest.tone !== 'error') {
                dismiss(latest.id);
              }
            }
          }}
        >
          {modalToasts.map((toast) => {
            const Icon = toast.icon ?? ICONS[toast.tone];
            return (
              <div
                key={toast.id}
                className={[
                  styles.toast,
                  styles[toast.tone],
                  toast.leaving ? styles.leaving : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-testid={`toast-${toast.tone}`}
                role="dialog"
                aria-modal="true"
              >
                <button
                  type="button"
                  className={styles.dismiss}
                  onClick={() => dismiss(toast.id)}
                  aria-label="Dismiss notification"
                >
                  <X size={18} aria-hidden="true" />
                </button>

                <span className={styles.badge} aria-hidden="true">
                  <Icon size={24} strokeWidth={2} />
                </span>

                <div className={styles.body}>
                  {toast.title ? (
                    <h3 className={styles.heading}>{toast.title}</h3>
                  ) : null}
                  <p className={styles.message}>{toast.message}</p>
                </div>

                <div className={styles.actions}>
                  {toast.action ? (
                    <>
                      <Button
                        variant="secondary"
                        onClick={() => dismiss(toast.id)}
                        fullWidth
                      >
                        Dismiss
                      </Button>
                      <Button
                        variant={toast.tone === 'error' ? 'danger' : 'primary'}
                        onClick={() => {
                          toast.action?.onClick();
                          dismiss(toast.id);
                        }}
                        fullWidth
                      >
                        {toast.action.label}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant={toast.tone === 'error' ? 'danger' : 'primary'}
                      onClick={() => dismiss(toast.id)}
                      fullWidth
                    >
                      {toast.tone === 'error' ? 'Close' : 'Got it'}
                    </Button>
                  )}
                </div>

                {toast.duration !== null && !toast.leaving ? (
                  <span
                    className={styles.rail}
                    aria-hidden="true"
                    style={{
                      animationDuration: `${toast.duration}ms`,
                      animationPlayState: paused ? 'paused' : 'running',
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </ToastContext>
  );
}
